import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  AssetRow,
  AssetTotals,
  AssetType,
  CreativeFilters,
  CreativeRow,
  CreativeTotals,
  GroupBy,
  Level,
  MediaType,
} from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const dash = '–';

// Snowflake hands back high-precision numerics as strings to avoid losing
// digits, so anything that formats, compares or sorts a measure has to coerce
// rather than type-check. Guarding on `typeof === 'number'` would blank out
// every money cell the moment the driver decided to preserve scale.
export function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const num = toNum;

export function fmtMoney(v: unknown): string {
  const n = num(v);
  if (n === null) return dash;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs)}`;
}

// Exact dollars for the totals footer, where rounding to $1.2k hides real money.
export function fmtMoneyExact(v: unknown): string {
  const n = num(v);
  if (n === null) return dash;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function fmtRatio(v: unknown): string {
  const n = num(v);
  return n === null ? dash : n.toFixed(2);
}

export function fmtPct(v: unknown, digits = 1): string {
  const n = num(v);
  return n === null ? dash : `${(n * 100).toFixed(digits)}%`;
}

export function fmtInt(v: unknown): string {
  const n = num(v);
  return n === null ? dash : Math.round(n).toLocaleString('en-US');
}

export function fmtDate(v: unknown): string {
  if (!v) return dash;
  const iso = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : dash;
}

export function fmtSeconds(v: unknown): string {
  const n = num(v);
  return n === null ? dash : `${n.toFixed(1)}s`;
}

export type Tone = 'good' | 'bad' | '';

// Severity is a separate channel from the indigo interaction accent (DESIGN.md),
// so these tones only ever colour text — never a control.
// Both coerce: these compare the raw row value, and a numeric string would
// compare as text ('0.9' >= 2 is false by luck, '10' < 1 is true by accident).
export function roasTone(value: unknown, breakeven = 1): Tone {
  const v = toNum(value);
  if (v === null) return '';
  if (v >= breakeven * 2) return 'good';
  if (v < breakeven) return 'bad';
  return '';
}

export function profitTone(value: unknown): Tone {
  const v = toNum(value);
  if (v === null) return '';
  if (v > 0) return 'good';
  if (v < 0) return 'bad';
  return '';
}

// Premium's formatting and thresholds are shared with the Meta Ads Insights
// dashboard, which reports the same metric.
export { fmtPremium, premiumTone, DEFAULT_PREMIUM_MAX } from '../../utils/premium';

// ---- hover math -------------------------------------------------------------
// The adjusted figures are the only numbers on this page that aren't reported by
// a source — they're computed here from ones that are. Each builder spells out
// that arithmetic for one row: the original value, what was done to it, and the
// result. Returning null when an input is missing keeps a cell from offering an
// explanation it can't actually give.
//
// `multiplier` comes from the API response, never a constant in this file — one
// copy of 1.2 in the codebase, on the server that applies it.

const money = fmtMoneyExact;

// Every printed equation is computed from the numbers it PRINTS, not from the
// full-precision ones behind them, so a reader who checks the arithmetic gets
// the stated answer. Deriving the result from unrounded inputs instead left
// equations that visibly don't add up — measured against production, a third of
// rows were off by a dollar here and by up to $53 in trueProfitMath below.

// True revenue as the hovers print it. Shared so the True revenue tooltip and
// the True profit tooltip can't quote figures a dollar apart for the same row.
function shownTrueRevenue(row: CreativeRow, multiplier: number): number | null {
  const meta = toNum(row.meta_purchase_value);
  return meta === null ? null : Math.round(Math.round(meta) * multiplier);
}

export function trueRevenueMath(row: CreativeRow, multiplier: number | null): string | null {
  const meta = toNum(row.meta_purchase_value);
  if (meta === null || toNum(row.true_revenue) === null || multiplier === null) return null;
  return `Meta purchase value ${money(Math.round(meta))} × ${multiplier} = ${money(
    shownTrueRevenue(row, multiplier) as number,
  )}`;
}

export function trueRoasMath(row: CreativeRow, multiplier: number | null): string | null {
  const metaRoas = toNum(row.meta_roas);
  if (metaRoas === null || toNum(row.true_roas) === null || multiplier === null) return null;
  const shownMeta = Number(metaRoas.toFixed(2));
  return `Meta ROAS ${shownMeta.toFixed(2)} × ${multiplier} = ${(shownMeta * multiplier).toFixed(
    2,
  )}`;
}

export function trueProfitMath(row: CreativeRow, multiplier: number | null): string | null {
  const trueRevenue = toNum(row.true_revenue);
  const result = toNum(row.true_profit);
  const revenue = toNum(row.revenue);
  const profit = toNum(row.profit);
  const spend = toNum(row.spend);
  if (
    trueRevenue === null ||
    result === null ||
    revenue === null ||
    profit === null ||
    spend === null ||
    revenue === 0 ||
    multiplier === null
  ) {
    return null;
  }
  const marginRate = (profit + spend) / revenue;
  // Stated as dollars rather than "× 73% margin". A margin rounded to a whole
  // percent does not reproduce the profit next to it — it was out by as much as
  // $53 — whereas these terms are integers that subtract exactly as shown.
  const shownRevenue = shownTrueRevenue(row, multiplier) ?? Math.round(trueRevenue);
  const contribution = Math.round(shownRevenue * marginRate);
  const cost = shownRevenue - contribution;
  const shownSpend = Math.round(spend);
  return (
    `True revenue ${money(shownRevenue)} − ${money(cost)} product cost = ${money(contribution)}\n` +
    `${money(contribution)} − ${money(shownSpend)} ad spend = ${money(contribution - shownSpend)}\n` +
    `Product cost is charged at this ad's own Triple Whale margin, ` +
    `${(marginRate * 100).toFixed(1)}% — (profit ${money(profit)} + spend ${money(
      spend,
    )}) ÷ revenue ${money(revenue)}`
  );
}

export function premiumMath(row: CreativeRow): string | null {
  const frequency = toNum(row.frequency);
  const premium = toNum(row.premium);
  if (frequency === null || premium === null) return null;
  const reach = toNum(row.reach);
  const base = `Frequency ${frequency.toFixed(2)} − 1 = ${Math.round(premium * 100)}%`;
  if (reach === null) return base;
  // Both derived from the two Meta numbers on this row, deliberately NOT from
  // row.impressions: that column is Triple Whale's impression count, and mixing
  // the sources would print a pair that doesn't divide out to the frequency
  // stated one line above.
  const impressions = Math.round(reach * frequency);
  const repeats = Math.max(0, impressions - reach);
  return (
    `${base}\n` +
    `${fmtInt(reach)} people reached, ${fmtInt(impressions)} impressions — ` +
    `${fmtInt(repeats)} were repeat views`
  );
}

export const MEDIA_LABELS: Record<MediaType, string> = {
  video: 'Video',
  static: 'Static',
  carousel: 'Carousel',
  unknown: 'Unknown',
};

// "leading" is doing real work in these labels: grouping ads by copy credits
// each ad's whole spend to whichever variant led it, which is not the same
// question as what that copy itself earned. Asset level answers that.
export const GROUP_BY_LABELS: Record<GroupBy, string> = {
  ad: 'Ad',
  creative: 'Creative asset',
  headline: 'Leading headline',
  primary_text: 'Leading primary text',
};

// Totals are derived from the rows actually on screen, so the summary can never
// describe a set the table isn't showing. Rates are re-derived from the summed
// numerators and denominators rather than averaged across rows.
function sumOf<T>(rows: T[], field: keyof T): number {
  return rows.reduce((total, row) => total + (toNum(row[field]) || 0), 0);
}

export function summarizeAds(rows: CreativeRow[]): CreativeTotals {
  const spend = sumOf(rows, 'spend');
  const revenue = sumOf(rows, 'revenue');
  const profit = sumOf(rows, 'profit');
  const orders = sumOf(rows, 'orders');
  const metaPurchaseValue = sumOf(rows, 'meta_purchase_value');
  const trueRevenue = sumOf(rows, 'true_revenue');

  return {
    rows: rows.length,
    ads: sumOf(rows, 'ad_count'),
    spend,
    revenue,
    profit,
    orders,
    impressions: sumOf(rows, 'impressions'),
    clicks: sumOf(rows, 'clicks'),
    sessions: sumOf(rows, 'sessions'),
    metaPurchaseValue,
    trueRevenue,
    // Summed rather than re-derived from one blended margin: rows with no Triple
    // Whale revenue have no true_profit at all, and a blended rate would invent
    // one for exactly those rows.
    trueProfit: sumOf(rows, 'true_profit'),
    roas: spend > 0 ? revenue / spend : null,
    poas: spend > 0 ? profit / spend : null,
    cpa: orders > 0 ? spend / orders : null,
    aov: orders > 0 ? revenue / orders : null,
    metaRoas: spend > 0 ? metaPurchaseValue / spend : null,
    trueRoas: spend > 0 ? trueRevenue / spend : null,
  };
}

export function summarizeAssets(rows: AssetRow[]): AssetTotals {
  const spend = sumOf(rows, 'spend');
  const purchaseValue = sumOf(rows, 'purchase_value');
  const purchases = sumOf(rows, 'purchases');
  const trueRevenue = sumOf(rows, 'true_revenue');

  return {
    rows: rows.length,
    assets: rows.length,
    ads: sumOf(rows, 'ad_count'),
    spend,
    impressions: sumOf(rows, 'impressions'),
    clicks: sumOf(rows, 'clicks'),
    purchases,
    purchaseValue,
    trueRevenue,
    metaRoas: spend > 0 ? purchaseValue / spend : null,
    metaCpa: purchases > 0 ? spend / purchases : null,
    trueRoas: spend > 0 ? trueRevenue / spend : null,
  };
}

// A rollup row describes several ads; the flat per-ad view describes exactly one.
export function isRollup(groupBy: GroupBy): boolean {
  return groupBy !== 'ad';
}

// Every date in this data is an advertiser-timezone day: Meta reports insights
// in Pacific time and the attribution tables follow it. Off UTC, "today" rolls
// over at 5pm local, so an afternoon visit would default the window to a day
// that hasn't started yet.
export const REPORTING_TIMEZONE = 'America/Los_Angeles';

export function isoDaysAgo(days: number): string {
  return dayjs().tz(REPORTING_TIMEZONE).subtract(days, 'day').format('YYYY-MM-DD');
}

export function isoToday(): string {
  return dayjs().tz(REPORTING_TIMEZONE).format('YYYY-MM-DD');
}

export const DATE_PRESETS: { label: string; days: number }[] = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

export const PAGE_SIZE_OPTIONS = [10, 25, 50];

export type ExportScope = 'page' | 'all';

// A CSV carries no filter state of its own, so everything needed to say what a
// file actually contains goes in its name: which table, how it was grouped, the
// window, the scope, and whether the API had already cut the set short. Without
// that, two exports taken a week apart are indistinguishable once they're
// sitting in a downloads folder — and an LLM reading one has no idea what it's
// reading. No extension: the grid appends `.csv`.
export function exportFileName(
  filters: Pick<CreativeFilters, 'level' | 'groupBy' | 'assetType' | 'start' | 'end'>,
  scope: ExportScope,
  rowCount: number,
  truncated: boolean,
): string {
  const grain =
    filters.level === 'asset' ? `asset-${filters.assetType}` : `ad-by-${filters.groupBy}`;
  const parts = [
    'creative-performance',
    grain,
    `${filters.start}_${filters.end}`,
    scope === 'page' ? `page-${rowCount}-rows` : `all-${rowCount}-rows`,
  ];
  // Only meaningful on a whole-set export: one page is a slice by definition,
  // so calling it truncated would say nothing.
  if (scope === 'all' && truncated) parts.push('truncated');
  return parts.join('-');
}

// Columns hidden until the user says otherwise: secondary attribution and
// diagnostic measures that make the default view noisy. Everything here is one
// tick away in the Columns dialog.
//
// Per level, because the two tables share almost no columns. Applying the ad
// list to the asset table would hide meta_roas — the only ROAS that exists at
// that grain, and the whole reason the view is there.
export const DEFAULT_HIDDEN_COLUMNS: Record<Level, string[]> = {
  ad: [
    // The Triple Whale figures and raw spend, superseded on the default view by
    // the true-* columns that carry the incrementality uplift. Still the place
    // to look when true revenue and Triple Whale disagree, so they are one tick
    // away in the Columns dialog rather than gone.
    'spend',
    'revenue',
    'profit',
    'roas',
    'poas',
    'meta_purchases',
    'attribution_gap',
    'impressions',
    'nc_roas',
    'nc_cpa',
    'meta_roas',
    // Premium's inputs. Premium itself is the number worth reading; these are
    // for checking it, and the cell's hover already shows both.
    'reach',
    'frequency',
    // Where an ad sits in the account. Redundant on screen — the identity cell
    // already names the ad — but the only way to tell an export which campaign
    // a row belongs to, so they exist as columns and default to hidden.
    'campaign_name',
    'adset_name',
    'ad_name',
  ],
  // The asset table is already narrow and every column on it is Meta-reported,
  // so there is nothing worth hiding by default.
  asset: [],
};

export const DEFAULT_FILTERS: CreativeFilters = {
  start: isoDaysAgo(6),
  end: isoToday(),
  mediaType: 'all',
  groupBy: 'ad',
  search: '',
  sort: 'spend',
  direction: 'desc',
  level: 'ad',
  assetType: 'headline',
  hideZeroSpend: true,
  pageSize: 10,
  rules: [],
};

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  headline: 'Headline',
  primary_text: 'Primary text',
  image: 'Image',
  video: 'Video',
};

// Deep link straight to the ad in Ads Manager, pre-filtered to that one ad.
export function adsManagerUrl(
  accountId: string | null,
  adId: string | null,
): string | null {
  if (!accountId || !adId) return null;
  return `https://adsmanager.facebook.com/adsmanager/manage/ads?act=${accountId}&selected_ad_ids=${adId}`;
}

export function videoUrl(videoId: string | null): string | null {
  return videoId ? `https://www.facebook.com/${videoId}` : null;
}

// post_id arrives as "<pageId>_<postId>", which only resolves as a permalink
// once it's split back apart.
export function postUrl(postId: string | null): string | null {
  if (!postId) return null;
  const [pageId, post] = postId.split('_');
  if (!pageId || !post) return null;
  return `https://www.facebook.com/${pageId}/posts/${post}`;
}

// Meta returns image assets as absolute URLs but video assets as a relative
// path like "/reel/123/".
export function assetUrl(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://www.facebook.com${url}`;
}

// FALLBACK ONLY. Rendering goes through the API's image proxy
// (`thumb_proxy_url` / `image_proxy_url`) — this is what's left for a row the
// API didn't mint a link for, which in practice means an ad row with no ad_id.
//
// Why the proxy exists at all: Meta hands back two shapes of creative image URL
// and neither is usable directly. Anything on *.fbcdn.net is pre-signed with an
// `oe=` expiry a few days out. The permanent
// `https://www.facebook.com/ads/image/?d=<hash>` endpoint doesn't expire, but it
// serves the full-resolution original under `no-store` and re-signs a different
// CDN target per request — so a 40px cell downloaded ~200 KB, uncached, on every
// render, and the loads the grid aborted mid-scroll read as dead images.
//
// The 64px proxy thumbnail (external*.fbcdn.net/emg1/…?url=…) carries the
// permanent URL inside its own `url` parameter, which is how the durable link is
// recovered from what's already stored. The backend now does the same resolution
// in creativeImage/creativeImageController.js — keep the two in step if either
// changes.
const PERMANENT_IMAGE_PREFIX = 'https://www.facebook.com/ads/image/';
const PROXY_THUMBNAIL_MARKER = '/emg1/';

function permanentImageUrl(url: string | null): string | null {
  if (!url || !url.startsWith('https://')) return null;
  if (url.startsWith(PERMANENT_IMAGE_PREFIX)) return url;
  let embedded: string | null = null;
  try {
    embedded = new URL(url).searchParams.get('url');
  } catch {
    return null;
  }
  return embedded && embedded.startsWith(PERMANENT_IMAGE_PREFIX) ? embedded : null;
}

export interface CreativeImage {
  url: string | null;
  // True only for Meta's 64px proxy thumbnail, which turns to mush if it's
  // upscaled to fill a preview panel. The other stored URLs are full-size.
  isThumbnail: boolean;
}

// The best image we can render for a creative: a permanent URL if either stored
// column yields one, otherwise the pre-signed link, which works until Meta's
// signature expires.
export function creativeImage(
  thumbnailUrl: string | null,
  imageUrl: string | null = null,
): CreativeImage {
  const permanent = permanentImageUrl(thumbnailUrl) || permanentImageUrl(imageUrl);
  const url = permanent || imageUrl || thumbnailUrl || null;
  return {
    url,
    isThumbnail: !!url && url.includes(PROXY_THUMBNAIL_MARKER),
  };
}

// What the grid cell should render: the proxy when the API minted one, otherwise
// whatever can be salvaged from the raw Meta columns.
export function creativeThumbnailSrc(
  proxyUrl: string | null,
  thumbnailUrl: string | null,
  imageUrl: string | null = null,
): string | null {
  return proxyUrl || creativeImage(thumbnailUrl, imageUrl).url;
}
